import React, { useState } from "react";
import api from "../services/api";
import { downloadPdf } from "../utils/downloadPdf";

const STATUS_CONFIG = {
  Pending:   { color: "#f59e0b", bg: "#fef3c7", icon: "🕓" },
  Bidding:   { color: "#f59e0b", bg: "#fef3c7", icon: "💰" },
  Assigned:  { color: "#3b82f6", bg: "#dbeafe", icon: "🚕" },
  Picked:    { color: "#3b82f6", bg: "#dbeafe", icon: "🚕" },
  Delivered: { color: "#10b981", bg: "#dcfce7", icon: "✅" },
  Cancelled: { color: "#ef4444", bg: "#fee2e2", icon: "❌" },
  Expired:   { color: "#94a3b8", bg: "#f1f5f9", icon: "⏳" },
};

function BookingSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);


  const [actionLoading, setActionLoading] = useState(null); // holds bookingId currently processing
  const [reassignModal, setReassignModal] = useState(null); // holds bookingId being reassigned
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedNewDriver, setSelectedNewDriver] = useState("");


  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/bookings/search", { params: { query: query.trim() } });
      setResults(res.data.bookings || []);
      setSearched(true);
    } catch (err) {
      console.log(err);
      setError("Search failed. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };



    const handleCancelRefund = async (bookingId) => {
    const reason = window.prompt("Reason for cancelling this booking (optional):");
    if (reason === null) return; // user hit Cancel on the prompt

    if (!window.confirm("Are you sure you want to cancel and refund this booking?")) return;

    try {
      setActionLoading(bookingId);
      const res = await api.post(`/booking/${bookingId}/cancel-refund`, { reason });
      alert(res.data.message);
      handleSearch({ preventDefault: () => {} });
    } catch (err) {
      alert(err.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceDelivered = async (bookingId) => {
    if (!window.confirm("Mark this booking as Delivered? This cannot be undone.")) return;

    try {
      setActionLoading(bookingId);
      const res = await api.post(`/booking/${bookingId}/force-delivered`);
      alert(res.data.message);
      handleSearch({ preventDefault: () => {} });
    } catch (err) {
      alert(err.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const openReassignModal = async (bookingId) => {
    setReassignModal(bookingId);
    setSelectedNewDriver("");
    try {
      const res = await api.get("/all-drivers?status=FREE");
      setAvailableDrivers(res.data.drivers || []);
    } catch (err) {
      setAvailableDrivers([]);
    }
  };

  const handleReassignConfirm = async () => {
    if (!selectedNewDriver) return alert("Select a driver first");

    try {
      setActionLoading(reassignModal);
      const res = await api.post(`/booking/${reassignModal}/reassign`, {
        newDriverId: selectedNewDriver,
      });
      alert(res.data.message);
      setReassignModal(null);
      handleSearch({ preventDefault: () => {} });
    } catch (err) {
      alert(err.response?.data?.message || "Reassign failed");
    } finally {
      setActionLoading(null);
    }
  };

  const canCancelRefund = (b) => !["Cancelled", "Delivered", "Bidding"].includes(b.status);
  const canReassign = (b) => ["Assigned", "Picked"].includes(b.status);
  const canForceDeliver = (b) => ["Assigned", "Picked"].includes(b.status);




  const renderStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || { color: "#64748b", bg: "#f1f5f9", icon: "•" };
    return (
      <span style={{ ...styles.badge, color: cfg.color, background: cfg.bg }}>
        <span style={{ marginRight: 6 }}>{cfg.icon}</span>
        {status}
      </span>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Booking Search</h1>
        <p style={styles.subtitle}>
          Search by Booking ID, customer phone, or driver phone — across all statuses
        </p>
      </div>

      <form onSubmit={handleSearch} style={styles.searchForm}>
        <input
          style={styles.searchInput}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter Booking ID, customer phone, or driver phone..."
        />
        <button style={styles.searchBtn} type="submit" disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </button>
      </form>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {searched && !loading && (
        <div style={styles.resultsWrap}>
          <div style={styles.resultsHeader}>
            {results.length} result{results.length !== 1 ? "s" : ""} found
          </div>

          {results.length === 0 ? (
            <div style={styles.emptyState}>No bookings matched your search.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Booking ID</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Customer</th>
                    <th style={styles.th}>Customer Phone</th>
                    <th style={styles.th}>Driver</th>
                    <th style={styles.th}>Driver Phone</th>
                    <th style={styles.th}>Pickup</th>
                    <th style={styles.th}>Drop</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Created</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((b) => (
                    <tr key={b._id} style={styles.tr}>
                      <td style={styles.td}><span style={styles.mono}>{b._id}</span></td>
                      <td style={styles.td}>{renderStatusBadge(b.status)}</td>
                      <td style={styles.td}>{b.user?.name || "—"}</td>
                      <td style={styles.td}>{b.user?.phone || "—"}</td>
                      <td style={styles.td}>{b.assignedDriver?.name || "—"}</td>
                      <td style={styles.td}>{b.assignedDriver?.phone || "—"}</td>
                      <td style={styles.td}>{b.pickupLocation}</td>
                      <td style={styles.td}>{b.dropLocation}</td>
                      <td style={styles.td}>{b.totalAmount ? `₹${b.totalAmount}` : "—"}</td>
                                            <td style={styles.td}>
                        {b.createdAt ? new Date(b.createdAt).toLocaleString() : "—"}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {canCancelRefund(b) && (
                            <button
                              style={styles.actionBtnCancel}
                              disabled={actionLoading === b._id}
                              onClick={() => handleCancelRefund(b._id)}
                            >
                              Cancel/Refund
                            </button>
                          )}
                          {canReassign(b) && (
                            <button
                              style={styles.actionBtnReassign}
                              disabled={actionLoading === b._id}
                              onClick={() => openReassignModal(b._id)}
                            >
                              Reassign
                            </button>
                          )}
                          {canForceDeliver(b) && (
                            <button
                              style={styles.actionBtnDeliver}
                              disabled={actionLoading === b._id}
                              onClick={() => handleForceDelivered(b._id)}
                            >
                              Force Delivered
                            </button>
                          )}

                          <button
  style={styles.actionBtnReceipt}
  onClick={() => downloadPdf(`/documents/trip-receipt/${b._id}`, `receipt-${b._id}.pdf`)}
>
  Receipt
</button>
                        </div>
                      </td>
                    </tr>
                  ))}

                </tbody>
              </table>
            </div>
          )}
                </div>
      )}

      {reassignModal && (
        <div style={styles.modalOverlay} onClick={() => setReassignModal(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Reassign Driver</h3>
            <p style={styles.modalSubtitle}>Select an available driver to take over this booking</p>

            <select
              style={styles.modalSelect}
              value={selectedNewDriver}
              onChange={(e) => setSelectedNewDriver(e.target.value)}
            >
              <option value="">-- Select driver --</option>
              {availableDrivers.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.name} — {d.phone} ({d.vehicleDetails?.type})
                </option>
              ))}
            </select>

            {availableDrivers.length === 0 && (
              <p style={{ fontSize: "13px", color: "#94a3b8" }}>No free drivers available right now.</p>
            )}

            <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
              <button style={styles.modalCancelBtn} onClick={() => setReassignModal(null)}>
                Cancel
              </button>
              <button
                style={styles.modalConfirmBtn}
                disabled={!selectedNewDriver || actionLoading === reassignModal}
                onClick={handleReassignConfirm}
              >
                Confirm Reassign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: "30px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  header: { marginBottom: "20px" },
  title: { margin: 0, fontSize: "28px", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.5px" },
  subtitle: { margin: "5px 0 0 0", color: "#64748b", fontSize: "15px" },
  searchForm: { display: "flex", gap: "12px", marginBottom: "20px" },
  searchInput: {
    flex: 1, padding: "14px 16px", borderRadius: "10px",
    border: "1px solid #e2e8f0", fontSize: "15px", outline: "none", background: "#fff",
  },
  searchBtn: {
    background: "#6366f1", color: "#fff", border: "none", padding: "0 28px",
    borderRadius: "10px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
  },
  errorBanner: {
    background: "#fee2e2", color: "#991b1b", padding: "12px 16px",
    borderRadius: "10px", fontSize: "14px", fontWeight: "600", marginBottom: "20px", width: "fit-content",
  },
  resultsWrap: {
    background: "#fff", borderRadius: "16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
  },
  resultsHeader: {
    padding: "16px 20px", fontSize: "14px", fontWeight: "600",
    color: "#475569", borderBottom: "1px solid #e2e8f0",
  },
  emptyState: { padding: "60px 20px", textAlign: "center", color: "#94a3b8", fontSize: "15px" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left", padding: "12px 20px", fontSize: "12px", fontWeight: "700",
    color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px",
    background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid #f1f5f9" },
  td: { padding: "14px 20px", fontSize: "14px", color: "#334155", whiteSpace: "nowrap" },
  mono: { fontFamily: "monospace", fontSize: "13px", color: "#475569" },
  badge: { display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: "700" },
  actionBtnCancel: { background: "#fee2e2", color: "#991b1b", border: "none", padding: "6px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "700", cursor: "pointer" },
  actionBtnReassign: { background: "#eef2ff", color: "#4338ca", border: "none", padding: "6px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "700", cursor: "pointer" },
  actionBtnDeliver: { background: "#dcfce7", color: "#166534", border: "none", padding: "6px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "700", cursor: "pointer" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modalContent: { background: "#fff", borderRadius: "16px", padding: "28px", width: "90%", maxWidth: "420px" },
  modalTitle: { margin: "0 0 6px 0", fontSize: "18px", color: "#1e293b" },
  modalSubtitle: { margin: "0 0 16px 0", fontSize: "13px", color: "#64748b" },
  modalSelect: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box" },
  modalCancelBtn: { flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontWeight: "600" },
  modalConfirmBtn: { flex: 1, padding: "12px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#fff", cursor: "pointer", fontWeight: "600" },
  actionBtnReceipt: { background: "#eef2ff", color: "#4338ca", border: "none", padding: "6px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "700", cursor: "pointer" },
};

export default BookingSearch;